<template>
  <div class="admin-panel task-page task-page--new-client">

    <!-- ── TOP BAR ───────────────────────────────────────────────── -->
    <admin-header
      variant="full"
      :show-back="true"
      :crumbs="[{ label: 'Edit Client' }]"
      :session-countdown="sessionCountdown"
      :formatted-countdown="formattedCountdown"
      @open-logout="openLogoutModal"
      @settings-saved="onSettingsSaved"
    />

    <!-- ── MAIN ──────────────────────────────────────────────────── -->
    <main class="ap-container ap-fade-in">

      <!-- Page hero -->
      <div class="tp-hero">
        <div class="tp-hero-icon">
          <fa-icon :icon="['fas', 'pen']" />
        </div>
        <div>
          <h1 class="tp-hero-title">Edit Client</h1>
          <p class="tp-hero-sub">Update client information and service configuration.</p>
        </div>
      </div>

      <!-- ── EDIT FORM ──────────────────────────────────────────── -->
      <div class="ap-nc-wrap">
        <aside class="ap-panel ap-nc-panel">
          <div class="ap-panel-head">
            <h3>Client Details</h3>
            <span v-if="loadingClient" class="ap-panel-meta">
              <fa-icon :icon="['fas', 'spinner']" spin /> Loading…
            </span>
          </div>

          <div v-if="loadingClient" class="ap-nc-form" style="padding:32px;text-align:center;color:var(--gray-500)">
            <fa-icon :icon="['fas', 'spinner']" spin style="font-size:24px" />
          </div>

          <form v-else class="ap-nc-form" style="padding:24px" @submit.prevent="validateAndSave">

            <!-- ── Section 1: Basic Info ──────────────────────── -->
            <div class="cdm-section">
              <div class="cdm-section-label">Basic Info</div>
              <div class="cdm-section-wrapper">
                <text-input ref="fieldClient" v-model="newClient" label="Client's Full Name" :icon="['fas', 'building']" placeholder="e.g. Super Crop Sdn Bhd" :required="true" />
                <p v-if="formErrors.client" class="ap-field-error">{{ formErrors.client }}</p>

                <text-input ref="fieldInitial" v-model="newInitial" label="Initial" :icon="['fas', 'stamp']" placeholder="e.g. TYC" :required="true" />
                <p v-if="formErrors.initial" class="ap-field-error">{{ formErrors.initial }}</p>

                <select-input
                  ref="fieldClientType"
                  v-model="newClientType"
                  label="Business Type"
                  :required="true"
                  :options="clientTypeOptions"
                  :icon="['fas', 'building']"
                  placeholder="Select client type…"
                />
                <p v-if="formErrors.clientType" class="ap-field-error">{{ formErrors.clientType }}</p>

                <div ref="fieldService" class="ap-field">
                  <label>Service <span class="ap-req">*</span></label>
                  <div class="ap-checkbox-group">
                    <label
                      v-for="svc in serviceCategories"
                      :key="svc.value"
                      class="ap-checkbox-item"
                      :class="['ap-checkbox-item--' + svc.value, { 'is-checked': newServiceCategories.includes(svc.value) }]"
                    >
                      <input type="checkbox" :value="svc.value" v-model="newServiceCategories" style="display:none" />
                      {{ svc.name }}
                    </label>
                  </div>
                  <p v-if="formErrors.service" class="ap-field-error" style="margin-top:6px">{{ formErrors.service }}</p>
                </div>

                <select-input
                  v-model="newStatus"
                  label="Status"
                  :options="statusOptions"
                  :icon="['fas', 'toggle-on']"
                  placeholder="Select status…"
                />
              </div>
            </div>

            <!-- ── Section 2: Statutory Info ─────────────────── -->
            <div class="cdm-section">
              <div class="cdm-section-label">Statutory Info</div>
              <div class="cdm-section-wrapper">
                <template v-if="newClientType !== 'salaried_person'">
                  <text-input v-model="newSsmNumberNew" label="SSM Number (New)" :icon="['fas', 'id-card']" placeholder="e.g. 202301012345" />
                  <text-input v-model="newSsmNumberOld" label="SSM Number (Old)" :icon="['fas', 'id-card']" placeholder="e.g. 1234567-A" />
                </template>

                <tax-input v-model="newTaxNumber" label="Tax Number" />

                <prefix-input v-if="newClientType !== 'salaried_person'" v-model="newENumber" label="E Number" prefix="E" placeholder="e.g. 0012345678" />

                <select-input
                  v-model="newIrbBranch"
                  label="IRB Branch"
                  :options="irbBranchOptions"
                  :icon="['fas', 'landmark']"
                  placeholder="Select branch…"
                />
                <select-input
                  v-if="!['salaried_person', 'partnership', 'sole_prop', 'llp'].includes(newClientType)"
                  v-model="newAuditor"
                  label="Auditor"
                  :options="auditorOptions"
                  :icon="['fas', 'user-tie']"
                  placeholder="Select auditor…"
                />
                <select-input
                  v-model="newYearEnd"
                  label="Year End"
                  :options="yearEndOptions"
                  :icon="['fas', 'calendar-alt']"
                  placeholder="Select month…"
                />
              </div>
            </div>

            <!-- ── Section 3: Address ─────────────────────────── -->
            <div class="cdm-section">
              <div class="cdm-section-label">Address</div>
              <div class="cdm-section-wrapper">
                <div class="ap-field">
                  <label>Business Address</label>
                  <textarea v-model="newBusinessAddress" class="ap-textarea" rows="3" placeholder="e.g. 123 Jalan Bukit Bintang, 55100 Kuala Lumpur" />
                </div>
                <div class="ap-field">
                  <label>Mailing Address</label>
                  <textarea v-model="newMailingAddress" class="ap-textarea" rows="3" placeholder="Same as business address, or P.O. Box…" />
                </div>
              </div>
            </div>

            <!-- ── Section 4: Important Dates ───────────────── -->
            <template v-if="newServiceCategories.includes('cosec') || newServiceCategories.includes('payroll_monthly')">
              <div class="cdm-section">
                <div class="cdm-section-label">Important Dates</div>
                <div class="cdm-section-wrapper">
                  <template v-if="newServiceCategories.includes('cosec')">
                    <date-input v-model="newIncorporationDate" label="Incorporation Date" :max-date="newInceptionDate" />
                    <date-input v-model="newInceptionDate" label="Inception Date" :min-date="newIncorporationDate" />
                    <select-input
                      v-model="newCosecTerminationType"
                      label="Cosec Termination Type"
                      :options="cosecTerminationTypeOptions"
                      :icon="['fas', 'sign-out-alt']"
                      placeholder="Select type…"
                    />
                    <date-input v-model="newCosecTerminationDate" label="Cosec Termination Date" />
                  </template>
                  <template v-if="newServiceCategories.includes('payroll_monthly')">
                    <select-input
                      ref="fieldPayday"
                      v-model="newPayday"
                      label="Payday"
                      :required="true"
                      :options="paydayOptions"
                      :icon="['fas', 'calendar-alt']"
                      placeholder="Select payday…"
                    />
                    <p v-if="formErrors.payday" class="ap-field-error">{{ formErrors.payday }}</p>
                  </template>
                </div>
              </div>
            </template>

            <!-- ── Section 5: Contacts ────────────────────────── -->
            <div class="cdm-section">
              <div class="cdm-section-label">Contacts</div>
              <div class="cdm-section-wrapper">
                <div ref="fieldContacts" class="ap-field">
                  <contact-cards v-model="newContactCards" :min-cards="1" :label-options="contactCategoryOptions" :card-errors="contactCardErrors" />
                  <p v-if="formErrors.contacts" class="ap-field-error" style="margin-top:6px">{{ formErrors.contacts }}</p>
                </div>
              </div>
            </div>

            <div v-if="generatedUsername" class="ap-preview ap-preview-col">
              <div class="ap-preview-row">
                <fa-icon :icon="['fas', 'user']" class="ap-preview-icon" />
                <span>Username</span>
                <code>{{ generatedUsername }}</code>
              </div>
            </div>

            <div class="ap-form-actions">
              <app-button
                variant="danger"
                size="lg"
                team="admin"
                :disabled="loadingClient"
                style="width: 100%"
                @click="showDeleteModal = true"
              >
                <fa-icon :icon="['fas', 'user-minus']" /> Delete Client
              </app-button>
              <app-button
                type="submit"
                variant="primary"
                size="lg"
                team="admin"
                :disabled="savingClient"
                style="width: 100%"
              >
                <fa-icon :icon="savingClient ? ['fas', 'spinner'] : ['fas', 'save']" :spin="savingClient" />
                {{ savingClient ? 'Saving…' : 'Save Changes' }}
              </app-button>
            </div>

          </form>
        </aside>
      </div>

    </main>

    <!-- ── MODALS ─────────────────────────────────────────────────── -->
    <alert-modal :show="showPopup" :message="popupMessage" :status="popupStatus" @close="closePopup" />

    <session-modal :show="showAdminSessionModal" @continue="continueAdminSession" @signout="signOutAdminSession">
      Your admin session has expired. Continue to refresh your session or sign out.
    </session-modal>

    <confirm-modal
      :show="showLogoutModal"
      title="Confirm Logout"
      confirm-label="Logout"
      :icon="['fas', 'sign-out-alt']"
      @cancel="closeLogoutModal"
      @confirm="confirmLogout"
    >Are you sure you want to logout?</confirm-modal>

    <confirm-modal
      :show="showDeleteModal"
      title="Delete Client"
      confirm-label="Delete"
      loading-label="Deleting…"
      :icon="['fas', 'user-minus']"
      :danger="true"
      :loading="deletingClient"
      @cancel="showDeleteModal = false"
      @confirm="deleteClient"
    >
      <template v-if="originalClient">
        Permanently remove <strong>{{ originalClient.full_name || originalClient.client }}</strong>
        ({{ originalClient.email }})? This action cannot be undone.
      </template>
    </confirm-modal>

  </div>
</template>

<script>
import adminSessionMixin from '@/mixins/adminSessionMixin'
import AdminHeader from '@/components/admin/AdminHeader.vue'
import { yearEndOptions, statusOptions } from '@/utils/clientOptions'
import DateInput from '@/components/form/DateInput.vue'
import TextInput from '@/components/form/TextInput.vue'
import SelectInput from '@/components/form/SelectInput.vue'
import ContactCards from '@/components/form/ContactCards.vue'
import TaxInput from '@/components/form/TaxInput.vue'
import PrefixInput from '@/components/form/PrefixInput.vue'
import AppButton from '@/components/shared/AppButton.vue'
import ConfirmModal from '@/components/shared/ConfirmModal.vue'
import SessionModal from '@/components/shared/SessionModal.vue'
import AlertModal from '@/components/shared/AlertModal.vue'

export default {
  name: 'admin-edit-client',
  mixins: [adminSessionMixin],
  components: { AdminHeader, AppButton, DateInput, TextInput, SelectInput, ContactCards, TaxInput, PrefixInput, ConfirmModal, SessionModal, AlertModal },
  metaInfo () {
    return { title: 'Tyng & Co | Edit Client', titleTemplate: null }
  },
  data () {
    return {
      clientId: null,
      originalClient: null,
      originalCategory: [],
      loadingClient: true,
      savingClient: false,
      // Form fields
      newClient: '',
      newInitial: '',
      newStatus: 'Pending',
      newClientType: '',
      newYearEnd: '',
      newServiceCategories: [],
      newTaxNumber: '',
      newENumber: '',
      newIrbBranch: '',
      newAuditor: '',
      irbBranches: ['Cheras', 'W.P. Kuala Lumpur', 'Bukit Mertajam', 'Petaling Jaya', 'Kuala Lumpur Bandar'],
      auditors: ['Marie J & Co', 'Cheng & Co', 'YYC PLT'],
      contactCategories: ['Internal Staff', 'Outsourced Account', 'Director', 'Shareholder'],
      newSsmNumberNew: '',
      newSsmNumberOld: '',
      newIncorporationDate: '',
      newInceptionDate: '',
      newCosecTerminationType: '',
      newCosecTerminationDate: '',
      newPayday: '',
      newBusinessAddress: '',
      newMailingAddress: '',
      cosecTerminationTypeOptions: [
        { value: 'Resigned', label: 'Resigned' },
        { value: 'Vacated', label: 'Vacated' }
      ],
      newContactCards: [{ label: '', name: '', phone: '', email: '', roles: [] }],
      serviceCategories: [
        { name: 'Tax', value: 'tax' },
        { name: 'TP Reminder', value: 'tp_reminder' },
        { name: 'Cosec', value: 'cosec' },
        { name: 'Incorporation', value: 'incorporation' },
        { name: 'Payroll Yearly', value: 'payroll_yearly' },
        { name: 'Payroll Reminder', value: 'payroll_reminder' },
        { name: 'Payroll Monthly', value: 'payroll_monthly' },
        { name: 'Accounting', value: 'accounting' }
      ],
      clientTypeOptions: [
        { value: 'sdn_bhd', label: 'Sdn Bhd' },
        { value: 'llp', label: 'LLP' },
        { value: 'sole_prop', label: 'Sole Prop' },
        { value: 'partnership', label: 'Partnership' },
        { value: 'salaried_person', label: 'Salaried Person' }
      ],
      yearEndOptions,
      statusOptions,
      // Form errors
      formErrors: { client: '', initial: '', clientType: '', service: '', payday: '', contacts: '' },
      contactCardErrors: [],
      // Popup
      showPopup: false,
      popupMessage: '',
      popupStatus: 'success',
      // Delete
      showDeleteModal: false,
      deletingClient: false,
      deleteSuccess: false
    }
  },
  computed: {
    irbBranchOptions () {
      return this.irbBranches.map(b => ({ value: b, label: b }))
    },
    auditorOptions () {
      return this.auditors.map(a => ({ value: a, label: a }))
    },
    contactCategoryOptions () {
      return this.contactCategories.map(c => ({ value: c, label: c }))
    },
    paydayOptions () {
      return Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))
    },
    generatedUsername () {
      if (!this.newClient.trim()) return ''
      const slug = this.newClient.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
      return slug || ''
    }
  },
  watch: {
    showPopup () { this.toggleBodyScroll() },
    showLogoutModal () { this.toggleBodyScroll() },
    showAdminSessionModal () { this.toggleBodyScroll() },
    showDeleteModal () { this.toggleBodyScroll() }
  },
  mounted () {
    this.startSessionTimer()
    this.fetchAdminSettings()
    this.loadClientData()
  },
  methods: {
    toggleBodyScroll () {
      const open = this.showPopup || this.showLogoutModal || this.showAdminSessionModal || this.showDeleteModal
      document.body.style.overflow = open ? 'hidden' : ''
    },

    isoToDMY (str) {
      if (!str) return ''
      const [y, m, d] = str.split('-')
      return (y && m && d) ? `${d}/${m}/${y}` : ''
    },

    parseDMY (str) {
      if (!str) return null
      const [d, m, y] = str.split('/')
      return (d && m && y) ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : null
    },

    async loadClientData () {
      this.loadingClient = true
      const id = parseInt(this.$route.params.id)
      try {
        const data = await this.adminFetch('/api/admin/clients/list.php')
        if (data === null) { this.pendingAdminRetry = () => this.loadClientData(); return }
        const client = (data.data || []).find(c => c.id === id)
        if (client) {
          this.populateForm(client)
        } else {
          this.$router.replace({ name: 'Admin-Panel', params: { lang: this.$i18n.locale } })
        }
      } catch (err) {
        console.error('Failed to load client:', err)
      } finally {
        this.loadingClient = false
      }
    },

    populateForm (client) {
      this.clientId = client.id
      this.originalClient = client
      this.newClient = client.client || ''
      this.newInitial = client.initial || ''
      this.newStatus = client.status || 'Active'
      this.newBusinessAddress = client.business_address || ''
      this.newMailingAddress = client.mailing_address || ''

      this.newContactCards = (client.contacts || []).map(c => ({
        label: c.label || '',
        name: c.name || '',
        phone: c.phone || '',
        email: c.email || '',
        roles: Array.isArray(c.roles) ? [...c.roles] : []
      }))
      if (!this.newContactCards.length) {
        this.newContactCards = [{ label: '', name: '', phone: '', email: '', roles: [] }]
      }

      const ci = client.company_identity || {}
      this.newClientType = ci.client_type || ''
      this.newYearEnd = ci.year_end ? String(ci.year_end) : ''
      this.newTaxNumber = ci.tax_number || ''
      this.newENumber = ci.e_number || ''
      this.newIrbBranch = ci.irb_branch || ''
      this.newAuditor = ci.auditor || ''
      this.newSsmNumberNew = ci.ssm_number_new || ''
      this.newSsmNumberOld = ci.ssm_number_old || ''
      this.newCosecTerminationType = ci.cosec_termination_type || ''
      this.newIncorporationDate = this.isoToDMY(ci.incorporation_date)
      this.newInceptionDate = this.isoToDMY(ci.inception_date)
      this.newCosecTerminationDate = this.isoToDMY(ci.cosec_termination_date)

      const cats = ci.category || []
      this.originalCategory = cats
      this.newServiceCategories = cats
        .map(c => c.name === 'Incorporation' ? 'incorporation' : c.name)
        .filter(n => ['tax', 'tp_reminder', 'cosec', 'incorporation', 'payroll_yearly', 'payroll_reminder', 'payroll_monthly', 'accounting'].includes(n))

      const pmCat = cats.find(c => c.name === 'payroll_monthly')
      if (pmCat) {
        const paydaySub = (pmCat.sub_category || []).find(s => s.name === 'payday')
        if (paydaySub && paydaySub.date && paydaySub.date.day) {
          this.newPayday = String(paydaySub.date.day)
        }
      }
    },

    validateAndSave () {
      if (this.validateForm()) {
        this.saveClient()
      } else {
        this.$nextTick(() => this.scrollToFirstError())
      }
    },

    scrollToFirstError () {
      const priority = [
        { key: 'client', ref: 'fieldClient' },
        { key: 'initial', ref: 'fieldInitial' },
        { key: 'clientType', ref: 'fieldClientType' },
        { key: 'service', ref: 'fieldService' },
        { key: 'payday', ref: 'fieldPayday' },
        { key: 'contacts', ref: 'fieldContacts' }
      ]
      for (const { key, ref } of priority) {
        if (this.formErrors[key]) {
          const r = this.$refs[ref]
          if (r) {
            const el = r.$el || r
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
          break
        }
      }
    },

    validateForm () {
      const errors = { client: '', initial: '', clientType: '', service: '', payday: '', contacts: '' }
      let valid = true

      const clientName = this.newClient.trim()
      if (!clientName || clientName.length < 3 || clientName.length > 50) {
        errors.client = "please enter client's full name"
        valid = false
      }
      if (!this.newInitial.trim()) {
        errors.initial = 'please enter initial'
        valid = false
      }
      if (!this.newClientType) {
        errors.clientType = 'please select a business type'
        valid = false
      }
      if (this.newServiceCategories.length === 0) {
        errors.service = 'please select minimum one service'
        valid = false
      }
      if (this.newServiceCategories.includes('payroll_monthly') && !this.newPayday) {
        errors.payday = 'please select a date'
        valid = false
      }

      const cards = this.newContactCards
      const cardErrors = cards.map(() => ({ label: false, name: false, phone: false, email: false, emailGroup: false }))
      let firstContactError = ''

      if (cards.length === 0) {
        errors.contacts = 'please add at least one contact'
        valid = false
      } else if (!cards.some(c => c.roles.includes('pic'))) {
        errors.contacts = 'please select one contact as PIC'
        cardErrors.forEach(e => { e.emailGroup = true })
        valid = false
      } else {
        for (let i = 0; i < cards.length; i++) {
          const card = cards[i]
          const isPic = card.roles.includes('pic')
          if (isPic) {
            if (!card.name.trim()) {
              cardErrors[i].name = true
              if (!firstContactError) firstContactError = 'please enter full name for PIC'
              valid = false
            }
            const picPhone = (card.phone || '').replace(/\D/g, '')
            if (!picPhone || picPhone.length < 7) {
              cardErrors[i].phone = true
              if (!firstContactError) firstContactError = 'please enter phone for PIC'
              valid = false
            } else if (picPhone.length > 15) {
              cardErrors[i].phone = true
              if (!firstContactError) firstContactError = 'please enter a valid phone number for PIC'
              valid = false
            }
            if (!card.email.trim()) {
              cardErrors[i].email = true
              if (!firstContactError) firstContactError = 'please enter email for PIC'
              valid = false
            } else if (!this.isValidEmailFormat(card.email)) {
              cardErrors[i].email = true
              if (!firstContactError) firstContactError = 'please enter a valid email for PIC'
              valid = false
            }
            if (!card.label) {
              cardErrors[i].label = true
              if (!firstContactError) firstContactError = 'please select contact category for PIC'
              valid = false
            }
          } else {
            if (!card.label) {
              cardErrors[i].label = true
              if (!firstContactError) firstContactError = 'please select contact category'
              valid = false
            }
            if (!card.email.trim()) {
              cardErrors[i].email = true
              if (!firstContactError) firstContactError = 'please enter email for contact'
              valid = false
            } else if (!this.isValidEmailFormat(card.email)) {
              cardErrors[i].email = true
              if (!firstContactError) firstContactError = 'please enter a valid email address'
              valid = false
            }
            if (!card.roles.some(r => ['to', 'cc', 'bcc', 'director'].includes(r))) {
              cardErrors[i].emailGroup = true
              if (!firstContactError) firstContactError = 'please select at least one email group'
              valid = false
            }
          }
        }
        if (!valid && firstContactError) errors.contacts = firstContactError
      }

      this.formErrors = errors
      this.contactCardErrors = cardErrors
      return valid
    },

    async saveClient () {
      this.savingClient = true
      try {
        const allCards = this.newContactCards.filter(c => c.name.trim() || c.email.trim() || c.phone.trim())
        const picCard = allCards.find(c => c.roles.includes('pic')) || allCards[0] || null

        const isSalaried = this.newClientType === 'salaried_person'
        const hideAuditor = ['salaried_person', 'partnership', 'sole_prop', 'llp'].includes(this.newClientType)
        const showCosec = this.newServiceCategories.includes('cosec')

        const newCategory = this.buildCategoryPayload()
        const mergedCategory = this.mergeCategories(newCategory, this.originalCategory)

        const companyIdentity = {
          client_type: this.newClientType,
          ...(this.newYearEnd ? { year_end: parseInt(this.newYearEnd) } : {}),
          ...(this.newTaxNumber && (this.newTaxNumber.number || this.newTaxNumber) ? { tax_number: this.newTaxNumber } : {}),
          ...(!isSalaried && this.newENumber && this.newENumber.number ? { e_number: this.newENumber } : {}),
          ...(this.newIrbBranch ? { irb_branch: this.newIrbBranch } : {}),
          ...(!hideAuditor && this.newAuditor ? { auditor: this.newAuditor } : {}),
          ...(!isSalaried && this.newSsmNumberNew.trim() ? { ssm_number_new: this.newSsmNumberNew.trim() } : {}),
          ...(!isSalaried && this.newSsmNumberOld.trim() ? { ssm_number_old: this.newSsmNumberOld.trim() } : {}),
          ...(showCosec ? {
            incorporation_date: this.parseDMY(this.newIncorporationDate) || null,
            inception_date: this.parseDMY(this.newInceptionDate) || null,
            cosec_termination_type: this.newCosecTerminationType || null,
            cosec_termination_date: this.parseDMY(this.newCosecTerminationDate) || null
          } : {}),
          category: mergedCategory
        }

        const originalClient = this.originalClient || {}

        const data = await this.adminFetch('/api/admin/clients/update.php', {
          method: 'POST',
          body: JSON.stringify({
            id: this.clientId,
            client: this.newClient,
            initial: this.newInitial,
            full_name: picCard ? picCard.name.trim() : (originalClient.full_name || ''),
            status: this.newStatus || 'Active',
            business_address: this.newBusinessAddress.trim() || null,
            mailing_address: this.newMailingAddress.trim() || null,
            contacts: allCards,
            company_identity: companyIdentity
          })
        })
        if (data === null) { this.pendingAdminRetry = () => this.saveClient(); return }
        this.openPopup(data.message || 'Client updated successfully.', 'success')
      } catch (err) {
        this.openPopup(err?.message || 'Failed to update client.', 'error')
      } finally {
        this.savingClient = false
      }
    },

    mergeCategories (newCats, origCats) {
      if (!origCats || !origCats.length) return newCats
      return newCats.map(newCat => {
        const origCat = origCats.find(o =>
          o.name === newCat.name || o.name === newCat.display_name ||
          (newCat.name === 'Incorporation' && o.name === 'Incorporation')
        )
        if (!origCat) return newCat
        const newSubs = (newCat.sub_category || []).map(newSub => {
          const origSub = (origCat.sub_category || []).find(o => o.name === newSub.name)
          if (!origSub) return newSub
          if (newSub.tasks) {
            return {
              ...newSub,
              tasks: newSub.tasks.map(task => {
                const origTask = (origSub.tasks || []).find(t => t.name === task.name)
                return origTask && origTask.status !== undefined ? { ...task, status: origTask.status } : task
              })
            }
          }
          return origSub.status !== undefined ? { ...newSub, status: origSub.status } : newSub
        })
        return { ...newCat, sub_category: newSubs }
      })
    },

    buildCategoryPayload () {
      const ye = parseInt(this.newYearEnd)
      const inc = this.parseDMY(this.newIncorporationDate)
      const LAST = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

      const addMo = (base, n) => {
        const m = ((base - 1 + n) % 12 + 12) % 12 + 1
        return { month: m, day: LAST[m] }
      }

      const shiftDays = (month, day, n) => {
        const d = new Date(2000, month - 1, day)
        d.setDate(d.getDate() + n)
        return { month: d.getMonth() + 1, day: d.getDate() }
      }

      const result = []
      const initStatus = { prev: 'pending', current: 'pending', next: 'pending' }

      const incDate = inc ? new Date(inc) : null
      const incMonth = incDate ? incDate.getMonth() + 1 : null
      const incDay = incDate ? incDate.getDate() : null

      if (this.newServiceCategories.includes('tax') && ye) {
        const tax8 = addMo(ye, 8)
        const mitrs = shiftDays(tax8.month, tax8.day, 30)
        const sub1m = addMo(ye, -1)
        const sub30d = shiftDays(ye, LAST[ye], -30)
        const cp204 = (sub1m.month * 100 + sub1m.day) <= (sub30d.month * 100 + sub30d.day) ? sub1m : sub30d
        result.push({
          id: 1,
          name: 'tax',
          display_name: 'Tax',
          sub_category: [
            { name: 'tax_filing', display_name: 'Tax Filing', date: tax8, status: { ...initStatus } },
            { name: 'mitrs', display_name: 'MITRS', date: mitrs, status: { ...initStatus } },
            { name: 'cp204', display_name: 'CP204', date: cp204, status: { ...initStatus } },
            { name: 'cp204a6', display_name: 'CP204A(6)', date: addMo(ye, 6), status: { ...initStatus } },
            { name: 'cp204a9', display_name: 'CP204A(9)', date: addMo(ye, 9), status: { ...initStatus } },
            { name: 'cp204a11', display_name: 'CP204A(11)', date: addMo(ye, 11), status: { ...initStatus } }
          ]
        })
      }

      if (this.newServiceCategories.includes('tp_reminder') && ye) {
        result.push({
          id: 5,
          name: 'tp_reminder',
          display_name: 'TP Reminder',
          sub_category: [
            { name: 'tp_reminder', display_name: 'TP Reminder', date: addMo(ye, 1), status: { ...initStatus } }
          ]
        })
      }

      if (this.newServiceCategories.includes('cosec')) {
        const arDate = (incMonth && incDay) ? shiftDays(incMonth, incDay, 29) : null
        const fsDate = ye ? addMo(ye, 7) : null
        const arReminder1st = incMonth ? addMo(incMonth, -5) : null
        const arReminder2nd = (incMonth && incDay) ? { month: incMonth, day: incDay } : null
        const arReminderLate1 = (incMonth && incDay) ? shiftDays(incMonth, incDay, 45) : null
        const arReminderLate2 = (incMonth && incDay) ? shiftDays(incMonth, incDay, 90) : null
        const afsReminder1st = ye ? shiftDays(ye, LAST[ye], 1) : null
        const afsReminder2nd = ye ? addMo(ye, 5) : null
        const afsReminderLate1 = fsDate ? shiftDays(fsDate.month, fsDate.day, 45) : null
        const afsReminderLate2 = fsDate ? shiftDays(fsDate.month, fsDate.day, 90) : null
        result.push({
          id: 2,
          name: 'cosec',
          display_name: 'Cosec',
          sub_category: [
            {
              name: 'compliance',
              display_name: 'Compliance',
              tasks: [
                { name: 'ar', display_name: 'AR', date: arDate },
                { name: 'fs', display_name: 'FS', date: fsDate }
              ]
            },
            {
              name: 'reminder',
              display_name: 'Reminder',
              tasks: [
                { name: 'ar_reminder_1st', display_name: 'AR Reminder (1st)', date: arReminder1st },
                { name: 'ar_reminder_2nd', display_name: 'AR Reminder (2nd)', date: arReminder2nd },
                { name: 'ar_reminder_late_1', display_name: 'AR Reminder (Late) 1', date: arReminderLate1 },
                { name: 'ar_reminder_late_2', display_name: 'AR Reminder (Late) 2', date: arReminderLate2 },
                { name: 'afs_reminder_1st', display_name: 'AFS Reminder (1st)', date: afsReminder1st },
                { name: 'afs_reminder_2nd', display_name: 'AFS Reminder (2nd)', date: afsReminder2nd },
                { name: 'afs_reminder_late_1', display_name: 'AFS Reminder (Late 1)', date: afsReminderLate1 },
                { name: 'afs_reminder_late_2', display_name: 'AFS Reminder (Late 2)', date: afsReminderLate2 }
              ]
            }
          ]
        })
      }

      if (this.newServiceCategories.includes('incorporation')) {
        result.push({
          id: 8,
          name: 'Incorporation',
          display_name: 'Incorporation',
          sub_category: [
            { name: 'submit_S58_236', display_name: 'Submit S58 & 236(2)', date: (incMonth && incDay) ? shiftDays(incMonth, incDay, 13) : null },
            { name: 'submit_S472_473', display_name: 'Submit S47(2) / S47(3)', date: (incMonth && incDay) ? shiftDays(incMonth, incDay, 13) : null },
            { name: 'submit_beneficial_ownership', display_name: 'Submit Beneficial Ownership', date: (incMonth && incDay) ? shiftDays(incMonth, incDay, 59) : null },
            { name: 'pay_form_m1_stamp_duty', display_name: 'Pay Form M1 Stamp Duty', date: (incMonth && incDay) ? shiftDays(incMonth, incDay, 29) : null },
            { name: 'pay_form_m3_stamp_duty', display_name: 'Pay Form M3 Stamp Duty', date: (incMonth && incDay) ? shiftDays(incMonth, incDay, 29) : null },
            { name: 'order_company_stamp', display_name: 'Order Company Stamp', status: 'pending' },
            { name: 'create_superior_file', display_name: 'Create Superior File', status: 'pending' },
            { name: 'create_physical_file', display_name: 'Create Physical File', status: 'pending' },
            { name: 'submit_form_1st_cp204', display_name: 'Submit Form 1st CP204', status: 'pending' }
          ]
        })
      }

      if (this.newServiceCategories.includes('payroll_yearly')) {
        result.push({
          id: 30,
          name: 'payroll_yearly',
          display_name: 'Payroll Yearly',
          sub_category: [
            { name: 'form_ea', display_name: 'Form EA', date: { day: 28, month: 2 } },
            { name: 'form_e', display_name: 'Form E', date: { day: 30, month: 4 } }
          ]
        })
      }

      if (this.newServiceCategories.includes('payroll_reminder')) {
        result.push({
          id: 31,
          name: 'payroll_reminder',
          display_name: 'Payroll Reminder',
          sub_category: [
            { name: 'payroll_reminder', display_name: 'Payroll Reminder', date: { day: 31, month: 1 } }
          ]
        })
      }

      if (this.newServiceCategories.includes('payroll_monthly')) {
        const payday = parseInt(this.newPayday) || null
        result.push({
          id: 32,
          name: 'payroll_monthly',
          display_name: 'Payroll Monthly',
          sub_category: [
            { name: 'payslip', display_name: 'Payslip', date: { day: 7 } },
            { name: 'pcb_epf_socso_eis_hrdc', display_name: 'PCB/EPF/SOCSO/EIS/HRDC', date: { day: 15 } },
            { name: 'payday', display_name: 'Payday', date: payday ? { day: payday } : null }
          ]
        })
      }

      if (this.newServiceCategories.includes('accounting') && ye) {
        result.push({
          id: 4,
          name: 'accounting',
          display_name: 'Accounting',
          sub_category: [
            { name: 'sst1', display_name: 'SST 1', date: addMo(ye, 1) },
            { name: 'sst2', display_name: 'SST 2', date: addMo(ye, 3) },
            { name: 'sst3', display_name: 'SST 3', date: addMo(ye, 5) },
            { name: 'sst4', display_name: 'SST 4', date: addMo(ye, 7) },
            { name: 'sst5', display_name: 'SST 5', date: addMo(ye, 9) },
            { name: 'sst6', display_name: 'SST 6', date: addMo(ye, 11) }
          ]
        })
      }

      return result
    },

    openPopup (message, status = 'success') {
      this.popupMessage = message || 'Action completed.'
      this.popupStatus = status
      this.showPopup = true
    },
    closePopup () {
      this.showPopup = false
      this.popupMessage = ''
      this.popupStatus = 'success'
      document.body.style.overflow = ''
      if (this.deleteSuccess) {
        this.$router.back()
      }
    },

    async deleteClient () {
      if (!this.originalClient) return
      this.deletingClient = true
      try {
        const data = await this.adminFetch('/api/admin/clients/delete.php', {
          method: 'POST',
          body: JSON.stringify({ username: this.originalClient.username })
        })
        if (data === null) { this.pendingAdminRetry = () => this.deleteClient(); return }
        this.showDeleteModal = false
        this.deleteSuccess = true
        this.openPopup(data.message || 'Client deleted successfully.', 'success')
      } catch (err) {
        this.showDeleteModal = false
        this.openPopup(err?.message || 'Failed to delete client.', 'error')
      } finally {
        this.deletingClient = false
      }
    },

    async fetchAdminSettings () {
      try {
        const data = await this.adminFetch('/api/admin/settings/get.php')
        if (data && data.success && data.data) {
          if (Array.isArray(data.data.irb_branches)) this.irbBranches = data.data.irb_branches
          if (Array.isArray(data.data.auditors)) this.auditors = data.data.auditors
          if (Array.isArray(data.data.contact_categories)) this.contactCategories = data.data.contact_categories
        }
      } catch (err) {
        console.error('Failed to load admin settings:', err)
      }
    },

    onSettingsSaved ({ branches, auditors, categories }) {
      this.irbBranches = branches
      this.auditors = auditors
      this.contactCategories = categories
    }
  }
}
</script>
